import { withEventContext } from "@codat/analytics";
import { Card, CircleAlertIcon, Typography, useTheme } from "@codat/orchard-ui";

import Button from "components/Button";
import SecondaryCta from "components/SecondaryCta";

import styles from "./WarningDialog.module.css";

interface WarningDialogProps {
  title?: string;
  subText?: string;
  primaryCtaProps: React.ComponentProps<typeof Button>;
  secondaryCtaProps: React.ComponentProps<typeof SecondaryCta>;
  ["data-testid"]?: string;
}

export function WarningDialogWithoutEventContext({
  title,
  subText,
  primaryCtaProps,
  secondaryCtaProps,
  "data-testid": dataTestId,
}: WarningDialogProps) {
  const { colors } = useTheme();

  return (
    <Card
      className={styles.root}
      data-testid={`${dataTestId ?? "warning-dialog"}`}
    >
      <header className={styles.header}>
        <CircleAlertIcon
          className={styles.icon}
          fillColor={colors["color-ui-negative"]}
        />
        {title && (
          <Typography
            className={styles.title}
            variant="h1"
            as="h1"
            data-testid={`${dataTestId ?? "warning-dialog"}-title`}
          >
            {title}
          </Typography>
        )}
      </header>
      <div className={styles.body}>
        {subText && (
          <Typography
            className={styles.subText}
            data-testid="warning-dialog-body"
            variant="p"
            as="p"
          >
            {subText}
          </Typography>
        )}
        <SecondaryCta
          {...secondaryCtaProps}
          data-testid={`${dataTestId ?? "warning-dialog"}-secondary-cta`}
        />
      </div>
      <footer className={styles.footer}>
        {primaryCtaProps && (
          <Button
            {...primaryCtaProps}
            data-testid={`${dataTestId ?? "warning-dialog"}-primary-cta`}
          />
        )}
      </footer>
    </Card>
  );
}

export const WarningDialog = withEventContext<
  WarningDialogProps & JSX.IntrinsicAttributes
>(WarningDialogWithoutEventContext, "Warning modal");
